// src/pages/Admin/AdminDashboard.tsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import GpxUpload from './GpxUpload';
import WaypointsBuilder from './WaypointsBuilder';
import { getProductById, getWaypointsById } from '../../globalService/api.service';
import { WayPoint } from '../../globalService/api.model';

const PREVIEW_USER_ID = 'PREVIEW';
const PREFIX = 'CC25';
const generateOrderId = () => Date.now().toString();

const AdminDashboard: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_HOST!;

  // Product state
  const [prodData, setProdData] = useState({
    is_active: true,
    id: PREFIX,
    name: '',
    images: '',
    url: '',
    totalDistanceKm: '',
    price: '',
    timeframeInDays: 0,
    customUserIcon: '',
    saved: false,
  });
  const [prodMsg, setProdMsg] = useState<string>('');
  const [loadId, setLoadId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  // For forcing re-fetch of waypoints & map
  const [previewReload, setPreviewReload] = useState<number>(0);

  // Existing waypoints
  const [initialWps, setInitialWps] = useState<WayPoint[]>([]);

  // Logout
  const handleExit = () => {
    auth.logout();
    navigate('/login', { replace: true });
  };

  // Load existing or stub product
  const loadChallenge = async () => {
    if (!loadId) return;
    setLoading(true);
    setProdMsg('');
    try {
      const prod = await getProductById(loadId);
      if (!prod) throw new Error('Not found');
      setProdData({
        is_active: true,
        id: PREFIX + prod.id,
        name: prod.name,
        images: prod.images || '',
        url: prod.url || '',
        totalDistanceKm: '',
        price: prod.price || '',
        timeframeInDays: 0,
        customUserIcon: '',
        saved: true,
      });
      setProdMsg('Loaded real challenge');
    } catch {
      setProdData(d => ({ ...d, id: PREFIX + loadId, saved: true }));
      setProdMsg('Loaded stub for ' + loadId);
    } finally {
      setLoading(false);
      setPreviewReload(r => r + 1);
    }
  };

  // Fetch waypoints on save/load and on each reload bump
  useEffect(() => {
    if (prodData.saved) {
      getWaypointsById(prodData.id, PREVIEW_USER_ID)
        .then(wps => setInitialWps(wps || []))
        .catch(() => setInitialWps([]));
    }
  }, [prodData.saved, prodData.id, previewReload]);

  // Handle product form changes
  const handleProdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    if (name === 'id') {
      const suffix = value.replace(new RegExp(`^${PREFIX}`), '');
      setProdData(d => ({ ...d, id: PREFIX + suffix }));
    } else {
      setProdData(d => ({
        ...d,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  // Create & enroll preview user
  const createProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    setProdMsg('');

    const requiredLen = PREFIX.length + 7;
    if (prodData.id.length !== requiredLen) {
      setProdMsg(`Product ID must be ${requiredLen} chars long (including prefix)`);
      return;
    }

    try {
      const payload = {
        is_active: prodData.is_active,
        id: prodData.id,
        name: prodData.name,
        images: prodData.images,
        url: prodData.url,
        totalDistanceKm: prodData.totalDistanceKm,
        price: prodData.price,
        timeframeInDays: prodData.timeframeInDays,
        customUserIcon: prodData.customUserIcon,
      };
      const res1 = await fetch(`${API}/medalmad/product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res1.ok) throw new Error(await res1.text());

      await fetch(`${API}/medalmad/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: PREVIEW_USER_ID,
          productIds: [payload.id],
          orderId: generateOrderId(),
        }),
      }).catch(() => {});

      setProdMsg('Product saved & preview enrolled');
      setProdData(d => ({ ...d, saved: true }));
      setPreviewReload(r => r + 1);
    } catch (err: any) {
      setProdMsg(`Error: ${err.message}`);
    }
  };

  // Callbacks to bump `previewReload`
  const handleGpxSuccess = () => setPreviewReload(r => r + 1);
  const handleWpSuccess  = () => setPreviewReload(r => r + 1);

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Admin Dashboard</h1>
        <button className="btn btn-outline-danger" onClick={handleExit}>
          Exit
        </button>
      </div>

      {/* Load existing */}
      <div className="row mb-4">
        <div className="col-12 col-md-6 col-lg-4 mx-auto">
          <label className="form-label">Load by ID (suffix only)</label>
          <div className="input-group">
            <span className="input-group-text">{PREFIX}</span>
            <input
              className="form-control"
              placeholder="e.g. ABC1234"
              value={loadId}
              onChange={e => setLoadId(e.target.value.trim())}
            />
            <button
              className="btn btn-outline-primary"
              onClick={loadChallenge}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Load'}
            </button>
          </div>
        </div>
      </div>
      {prodMsg && <div className="alert alert-info">{prodMsg}</div>}

      <div className="row g-4">
        {/* Product form */}
        <div className="col-md-4">
          <div className="card border-primary h-100">
            <div className="card-header bg-primary text-white">
              Create / Edit Product
            </div>
            <div className="card-body">
              <form onSubmit={createProduct}>
                {/* Active */}
                <div className="form-check mb-3">
                  <input
                    type="checkbox"
                    name="is_active"
                    className="form-check-input"
                    checked={prodData.is_active}
                    onChange={handleProdChange}
                    disabled={prodData.saved}
                  />
                  <label className="form-check-label">Active</label>
                </div>

                {/* ID */}
                <div className="mb-3">
                  <label className="form-label">Product ID Suffix (7 chars)</label>
                  <div className="input-group">
                    <span className="input-group-text">{PREFIX}</span>
                    <input
                      name="id"
                      className="form-control"
                      value={prodData.id.slice(PREFIX.length)}
                      onChange={handleProdChange}
                      required
                      disabled={prodData.saved}
                      maxLength={7}
                      pattern="[A-Za-z0-9]{7}"
                      title="Exactly 7 alphanumeric characters"
                    />
                  </div>
                </div>

                {/* Name */}
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input
                    name="name"
                    className="form-control"
                    value={prodData.name}
                    onChange={handleProdChange}
                    disabled={prodData.saved}
                    required
                  />
                </div>

                {/* Image */}
                <div className="mb-3">
                  <label className="form-label">Add Challenge Catalogue Image</label>
                  <input
                    name="images"
                    className="form-control"
                    value={prodData.images}
                    onChange={handleProdChange}
                    disabled={prodData.saved}
                  />
                </div>

                {/* Website */}
                <div className="mb-3">
                  <label className="form-label">Add a Website</label>
                  <input
                    name="url"
                    type="url"
                    className="form-control"
                    placeholder="https://"
                    value={prodData.url}
                    onChange={handleProdChange}
                    disabled={prodData.saved}
                  />
                </div>

                {/* Distance */}
                <div className="mb-3">
                  <label className="form-label">Enter Your Challenge Distance (km)</label>
                  <input
                    name="totalDistanceKm"
                    type="number"
                    className="form-control"
                    value={prodData.totalDistanceKm}
                    onChange={handleProdChange}
                    disabled={prodData.saved}
                    required
                  />
                </div>

                {/* Price */}
                <div className="mb-3">
                  <label className="form-label">Price</label>
                  <input
                    name="price"
                    className="form-control"
                    value={prodData.price}
                    onChange={handleProdChange}
                    disabled={prodData.saved}
                  />
                </div>

                {/* Timeframe */}
                <div className="mb-3">
                  <label className="form-label">Timeframe (days)</label>
                  <input
                    name="timeframeInDays"
                    type="number"
                    className="form-control"
                    value={prodData.timeframeInDays}
                    onChange={handleProdChange}
                    disabled={prodData.saved}
                  />
                </div>

                {/* Icon */}
                <div className="mb-3">
                  <label className="form-label">Custom User Icon URL</label>
                  <input
                    name="customUserIcon"
                    className="form-control"
                    value={prodData.customUserIcon}
                    onChange={handleProdChange}
                    disabled={prodData.saved}
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-success w-100"
                  disabled={prodData.saved}
                >
                  Save & Enrol Preview
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* GPX Upload */}
        <div className="col-md-4">
          <div className="card border-info h-100">
            <div className="card-header bg-info text-white">STEP 2</div>
            <div className="card-body">
              <GpxUpload
                productId={prodData.id}
                disabled={!prodData.saved}
                onUploadSuccess={handleGpxSuccess}
              />
            </div>
          </div>
        </div>

        {/* Waypoints */}
        <div className="col-md-4">
          <div className="card border-warning h-100">
            <div className="card-header bg-warning text-dark">STEP 3</div>
            <div className="card-body">
              <WaypointsBuilder
                productId={prodData.id}
                disabled={!prodData.saved}
                initialWaypoints={initialWps}
                onSaveSuccess={handleWpSuccess}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Map Preview */}
      {prodData.saved && (
        <div className="mt-5">
          <h2 className="mb-3">Map Preview</h2>
          <div className="ratio ratio-16x9">
            <iframe
              key={previewReload}
              src={`https://platform.virtual-challenge.com/map/medalmad/${prodData.id}/${PREVIEW_USER_ID}?r=${previewReload}`}
              title="Route Preview"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
